import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Users, ShieldCheck, ChevronDown, Loader2, Mail, Link2, Trash2 } from "lucide-react";
import { InviteForm } from "@/components/team/InviteForm";

export function GerentesTab({ members, onRefresh }) {
  const { t } = useTranslation();
  const gerentes = members.filter(m => m.role === "gerente" || m.role === "gerente_manager");
  const { authAxios } = useAuth();
  const [showForm, setShowForm] = useState(false);
  const [formRole, setFormRole] = useState("gerente");
  const [resending, setResending] = useState(null);

  const remove = async (id, name) => {
    if (!window.confirm(t("team.removeConfirm", { name }))) return;
    try {
      await authAxios.delete(`/deliverers/team/members/${id}`);
      toast.success(t("team.memberRemoved"));
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || t("team.removeError")); }
  };

  const resendInvite = async (m) => {
    setResending(m.id);
    try {
      await authAxios.post(`/deliverers/team/members/${m.id}/resend-invite`);
      toast.success(t("team.inviteResent", { email: m.email }));
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || t("team.resendError")); }
    setResending(null);
  };

  const copyInvite = (m) => {
    if (m.invite_token) {
      navigator.clipboard.writeText(`${window.location.origin}/invite/${m.invite_token}`);
      toast.success(t("team.linkCopied"));
    }
  };

  return (
    <div data-testid="gerentes-tab">
      <div className="flex items-center gap-2 mb-4">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-700 font-bold uppercase gap-2" data-testid="invite-gerente-btn">
              {t("team.inviteManager")} <ChevronDown className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem onClick={() => { setFormRole("gerente_manager"); setShowForm(true); }}>
              <ShieldCheck className="w-4 h-4 mr-2 text-emerald-600" />{t("team.managerFull")}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => { setFormRole("gerente"); setShowForm(true); }}>
              <Users className="w-4 h-4 mr-2 text-blue-500" />{t("team.managerCommon")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="bg-white rounded-lg border overflow-x-auto">
        <table className="w-full text-sm min-w-[480px]">
          <thead>
            <tr className="border-b text-gray-500 text-xs uppercase">
              <th className="text-left p-3 font-medium">{t("team.colName")}</th>
              <th className="text-left p-3 font-medium">{t("team.colEmail")}</th>
              <th className="text-left p-3 font-medium">{t("team.colRole")}</th>
              <th className="text-center p-3 font-medium">{t("team.colStatus")}</th>
              <th className="p-3 w-32"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {gerentes.map(m => (
              <tr key={m.id} className="hover:bg-gray-50">
                <td className="p-3 font-medium">{m.name}</td>
                <td className="p-3 text-xs text-gray-500">{m.email || "-"}</td>
                <td className="p-3">
                  {m.role === "gerente_manager"
                    ? <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">{t("team.roleMgr")}</Badge>
                    : <Badge className="bg-blue-100 text-blue-700 text-[10px]">{t("team.roleCommon")}</Badge>
                  }
                </td>
                <td className="p-3 text-center">
                  {m.invite_accepted ? (
                    <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">{t("team.statusActive")}</Badge>
                  ) : m.invite_status === "sent" ? (
                    <Badge className="bg-blue-100 text-blue-700 text-[10px]">{t("team.statusSent")}</Badge>
                  ) : m.invite_status === "error" ? (
                    <Badge className="bg-red-100 text-red-700 text-[10px]" title={m.invite_error}>{t("team.statusError")}</Badge>
                  ) : (
                    <Badge className="bg-amber-100 text-amber-700 text-[10px]">{t("team.statusPending")}</Badge>
                  )}
                </td>
                <td className="p-3 flex items-center gap-1">
                  {!m.invite_accepted && m.email && (
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-blue-600"
                      onClick={() => resendInvite(m)} disabled={resending === m.id} title={t("team.inviteResent", { email: m.email })}>
                      {resending === m.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                    </Button>
                  )}
                  {!m.invite_accepted && m.invite_token && (
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-emerald-600" onClick={() => copyInvite(m)} title={t("team.linkCopied")}>
                      <Link2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400" onClick={() => remove(m.id, m.name)}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </td>
              </tr>
            ))}
            {gerentes.length === 0 && (
              <tr><td colSpan={5} className="p-8 text-center text-gray-400">{t("team.noManagers")}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="uppercase font-bold">
              {formRole === "gerente_manager" ? t("team.inviteManagerFull") : t("team.inviteManagerCommon")}
            </DialogTitle>
          </DialogHeader>
          <InviteForm role={formRole} onClose={() => setShowForm(false)} onRefresh={onRefresh} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
